// "Publish to reports" (2026-11-01, GPS feed Stage 2; admins only, checked on the server): preview what this StatSports
// session writes into the GPS & Load report session, confirm, see the status ("changed since publish" after an edit:
// republish explicitly), unpublish. The choices the preview asks for: who a recording belongs to (a swapped vest:
// the CSV's player by default), whether a CSV-only player keeps his rows (yes by default), goalkeeper sessions (left out
// by default). Kept CSV rows (above Sonra, hand additions, corrected max speed, invalid windows) are listed, never refused.
import React, { useCallback, useEffect, useState } from 'react';
import { previewFeed, getFeedStatus, publishFeed, unpublishFeed } from '../../../api';
import { fmtNum } from './seShared';

const Err = ({ msg }) => (msg ? <div className="se-error" style={{ marginTop: 6 }}>{msg}</div> : null);
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const STATE_PILL = { 'not published': '', published: 'se-pill--est', 'changed since publish': 'se-pill--warn' };

// the options a preview/publish sends, from the stored ones (status) or the defaults
export const startOptions = stored => ({
  goalkeepers: !!(stored && stored.goalkeepers), blocks: !!(stored && stored.blocks), gpsSessionId: stored && stored.gpsSessionId != null ? stored.gpsSessionId : null,
  owners: {}, dropCsvPlayers: ((stored && stored.dropCsvPlayers) || []).map(Number),
});

export function rowsLine(plan) {
  if (!plan) return '';
  return `writes ${plural(plan.write.sessionRows, 'session row')} and ${plural(plan.write.drillRows, 'drill row')}; replaces ${plan.replace.sessionRows} + ${plan.replace.drillRows} CSV rows `
    + `(kept for unpublish); keeps ${plan.keep.sessionRows} + ${plan.keep.drillRows} CSV rows as they are`
    + `${plan.superseded && plan.superseded.sessionRows + plan.superseded.drillRows ? `; ${plan.superseded.sessionRows + plan.superseded.drillRows} of the replaced rows are KNVB estimates superseded by measured halves` : ''}`;
}

export default function PublishPanel({ token, sessionId, labelOfPlayer, onChanged, onClose }) {
  const [status, setStatus] = useState(null);
  const [options, setOptions] = useState(null);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(() => getFeedStatus(token, sessionId).then(s => { setStatus(s); return s; }), [token, sessionId]);
  useEffect(() => {
    let live = true;
    setStatus(null); setPreview(null); setOptions(null); setErr(''); setMsg('');
    loadStatus().then(s => { if (live) setOptions(startOptions(s.options)); }).catch(e => live && setErr(e.message));
    return () => { live = false; };
  }, [loadStatus]);
  useEffect(() => {
    if (!options) return undefined;
    let live = true;
    setPreview(null);
    previewFeed(token, sessionId, options).then(p => live && setPreview(p)).catch(e => live && setErr(e.message));
    return () => { live = false; };
  }, [token, sessionId, options]);

  const reload = () => { setOptions(o => ({ ...o })); return loadStatus(); };
  const act = (fn, done) => {
    setBusy(true); setErr(''); setMsg('');
    fn().then(r => { setMsg(done(r)); return reload(); }).then(() => onChanged && onChanged())
      .catch(e => { setErr(e.message); if (e.status === 409) reload().catch(() => {}); })
      .finally(() => setBusy(false));
  };
  const state = status ? status.state : null;
  const published = state && state !== 'not published';
  const publish = () => {
    const what = published ? 'Republish' : 'Publish';
    const ok = window.confirm(`${what} this session to the GPS & Load reports?\n\n${rowsLine(preview.plan)}.\n\n`
      + 'While it is published, CSV uploads, deletes and max-speed edits of the report session are refused; unpublish puts the CSV rows back exactly.');
    if (ok) act(() => publishFeed(token, sessionId, options, preview.inputHash), r => `${r.republished ? 'Republished' : 'Published'}${r.created ? ' (new report session)' : ''}: ${rowsLine(r.summary)}.`);
  };
  const unpublish = () => {
    const ok = window.confirm('Unpublish this session?\n\nThe published rows are removed and the CSV rows come back exactly as they were (a note written since stays).');
    if (ok) act(() => unpublishFeed(token, sessionId), r => `Unpublished: ${r.deleted.data + r.deleted.drills} rows removed, ${r.restored.data + r.restored.drills} CSV rows back`
      + `${r.sessionRemoved ? '; the session it created is removed' : r.sessionKeptBecause ? `; the session stays (${r.sessionKeptBecause})` : ''}.`
      + `${r.mss && r.mss.skipped.length ? ` MSS values not put back for ${r.mss.skipped.map(x => `${labelOfPlayer(x.playerId)} (${x.reason})`).join(', ')}.` : ''}`);
  };
  const setOwner = (sp, pid) => setOptions(o => ({ ...o, owners: { ...o.owners, [sp]: Number(pid) } }));
  const toggleDrop = pid => setOptions(o => ({ ...o, dropCsvPlayers: o.dropCsvPlayers.includes(pid) ? o.dropCsvPlayers.filter(x => x !== pid) : [...o.dropCsvPlayers, pid] }));

  const p = preview;
  const blocks = p ? p.warnings.filter(w => w.severity === 'block') : [];
  const upToDate = !!(p && p.published && p.published.upToDate);   // with the options as shown now (a changed choice → republish)
  return (
    <div className="se-sub" data-testid="publish-panel">
      <div className="se-bar" style={{ justifyContent: 'space-between' }}>
        <strong>Publish to reports</strong>
        {state && <span className={`se-pill ${STATE_PILL[state] || ''}`} data-testid="publish-state">{state}</span>}
      </div>
      {status && status.publication && (
        <div className="se-muted">published {String(status.publication.publishedAt).slice(0, 16).replace('T', ' ')} into report session #{status.publication.reportSessionId}
          {status.publication.createdSession ? ' (created by the publish)' : ''}{state === 'changed since publish' ? ': the periods or the data changed since; republish to update the reports' : ''}</div>
      )}
      <Err msg={err} />
      {msg && <div className="se-banner" style={{ marginTop: 6 }}>{msg}</div>}
      {!p ? (!err && <div className="se-muted">Preparing the preview…</div>) : (
        <>
          <div style={{ marginTop: 6 }}>
            Report session: {p.session.create ? <strong>a new {p.session.sessionType} session</strong> : <strong>#{p.session.id}</strong>} · {p.session.date} · {p.session.title}
            {p.link.overlap != null && <span className="se-muted"> · player overlap {Math.round(p.link.overlap * 100)} %</span>}
          </div>
          <div className="se-muted" data-testid="publish-rows">{rowsLine(p.plan)}</div>
          <div className="se-muted">{p.link.feeds.map(f => `${f.name} (${f.role}${f.included ? '' : ', left out'})`).join(' · ')}</div>
          <label className="se-muted"><input type="checkbox" checked={options.goalkeepers} onChange={e => setOptions(o => ({ ...o, goalkeepers: e.target.checked }))} /> include goalkeeper sessions</label>
          {/* timed blocks (2026-11-03): the 5-minute periods made with "Split into blocks"; left out unless ticked (Q11) */}
          <label className="se-muted" style={{ marginLeft: 12 }}><input type="checkbox" checked={options.blocks} onChange={e => setOptions(o => ({ ...o, blocks: e.target.checked }))} /> include timed blocks (5-minute periods)</label>

          {blocks.map(w => <div key={w.code} className="se-error" style={{ marginTop: 6 }}>{w.message}</div>)}
          <ul style={{ margin: '6px 0', paddingLeft: 18 }}>
            {p.warnings.filter(w => w.severity !== 'block').map((w, i) => (
              <li key={`${w.code}-${i}`} className="se-muted">{w.message}{w.count != null ? ` (${w.count})` : ''}{w.players ? `: ${w.players.map(labelOfPlayer).join(', ')}` : ''}</li>
            ))}
          </ul>

          {p.owners && p.owners.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <strong>Recording belongs to</strong>
              {p.owners.map(o => {
                const choices = [...new Set([o.playerId, o.statsportsPlayerId].filter(x => x != null))];
                return (
                  <div key={o.sessionPlayerId} className="se-bar" style={{ marginTop: 4 }}>
                    <span className="se-muted">the unit StatSports maps to {o.statsportsPlayerId != null ? labelOfPlayer(o.statsportsPlayerId) : 'nobody'}:</span>
                    <select className="se-input" aria-label="recording belongs to" value={o.playerId} disabled={busy} onChange={e => setOwner(o.sessionPlayerId, e.target.value)}>
                      {choices.map(pid => <option key={pid} value={pid}>{labelOfPlayer(pid)}{pid === o.statsportsPlayerId ? ' (StatSports mapping)' : ' (the CSV\'s player)'}</option>)}
                    </select>
                    <span className="se-muted">{o.how === 'default' ? 'default' : 'chosen'}</span>
                  </div>
                );
              })}
            </div>
          )}

          {p.csvOnlyPlayers.filter(c => c.sameRecordingAs == null).length > 0 && (
            <div style={{ marginTop: 6 }}>
              <strong>CSV rows without StatSports data</strong>
              {p.csvOnlyPlayers.filter(c => c.sameRecordingAs == null).map(c => (
                <div key={c.playerId}>
                  <label><input type="checkbox" checked={!options.dropCsvPlayers.includes(c.playerId)} disabled={busy} onChange={() => toggleDrop(c.playerId)} aria-label={`keep CSV rows of ${labelOfPlayer(c.playerId)}`} />
                    {' '}keep the CSV rows of {labelOfPlayer(c.playerId)}</label>
                  <span className="se-muted"> · {fmtNum(c.csvTd, 0)} m{c.imputed ? ` · estimated${c.method ? ` (${c.method})` : ''}` : ''}</span>
                </div>
              ))}
            </div>
          )}

          {(p.completionCandidates.length > 0 || p.maxSpeedEdits.length > 0 || p.invalidWindowRows.length > 0) && (
            <details style={{ marginTop: 6 }}>
              <summary>CSV rows kept or marked ({p.completionCandidates.length + p.maxSpeedEdits.length + p.invalidWindowRows.length})</summary>
              <table className="se-table"><tbody>
                {p.completionCandidates.map((c, i) => (
                  <tr key={`c${i}`}><td className="l">{labelOfPlayer(c.playerId)}</td><td className="l">{c.title}</td>
                    <td className="l">{c.row === 'extra' ? 'not in Sonra: kept' : `CSV ${fmtNum(c.csvTd, 0)} m > Sonra ${fmtNum(c.newTd, 0)} m: kept`}</td></tr>
                ))}
                {p.maxSpeedEdits.map((e, i) => (
                  <tr key={`m${i}`}><td className="l">{labelOfPlayer(e.playerId)}</td><td className="l">{e.title}{e.startTime ? ` ${e.startTime.slice(0, 5)}` : ''}</td>
                    <td className="l">max speed {fmtNum(e.csvKmh, 1)} km/h kept (Sonra {fmtNum(e.sonraKmh, 1)}), marked manual</td></tr>
                ))}
                {p.invalidWindowRows.map((r, i) => (
                  <tr key={`w${i}`}><td className="l">{labelOfPlayer(r.playerId)}</td><td className="l">{r.title}</td><td className="l">Sonra's values, marked "invalid window"</td></tr>
                ))}
              </tbody></table>
            </details>
          )}

          {/* match max speed (gaps build Stage C): his session row and Entire Session - Live row get the max of his own halves and runs */}
          {p.plan && p.plan.maxSpeedRule && p.plan.maxSpeedRule.applies && p.plan.maxSpeedRule.changes.length > 0 && (
            <details style={{ marginTop: 6 }} data-testid="max-speed-rule">
              <summary>Match max speed from his own halves and runs: {p.plan.maxSpeedRule.changes.length} change{p.plan.maxSpeedRule.changes.length === 1 ? '' : 's'}, {p.plan.maxSpeedRule.empty} empty</summary>
              <table className="se-table"><tbody>
                {p.plan.maxSpeedRule.changes.map(c => (
                  <tr key={c.playerId}><td className="l">{labelOfPlayer(c.playerId)}</td>
                    <td className="l">{c.before == null ? 'empty' : `${fmtNum(c.before, 1)} km/h`} → {c.after == null ? 'empty (no half, no run)' : `${fmtNum(c.after, 1)} km/h`}</td></tr>
                ))}
              </tbody></table>
            </details>
          )}

          <div className="se-bar" style={{ marginTop: 8 }}>
            <button className="se-btn se-btn--primary" disabled={busy || !p.canPublish || upToDate} onClick={publish}
              title={upToDate ? 'already published and up to date' : 'asks for confirmation first'}>{published ? 'Republish…' : 'Publish…'}</button>
            {published && <button className="se-btn" disabled={busy} onClick={unpublish}>Unpublish…</button>}
            <button className="se-btn" onClick={onClose}>Close</button>
          </div>
        </>
      )}
    </div>
  );
}
